import {
  createContext,
  PropsWithChildren,
  useCallback,
  use,
  useMemo,
} from "react";
import { getUsersCollection, IUser } from "integrations/firebase/collections";
import { useCollectionData } from "react-firebase-hooks/firestore";
import { getDocs, orderBy, query, where } from "firebase/firestore";

import {
  AccountOrganisationFileResponse,
  AcpExportResponse,
  OrganisationUserCreateRequest,
  AccountUserResponse,
  FirestoreOrganisationSettings,
} from "reedy-data/api";
import { FirestoreOrganisation, UserRole } from "reedy-data/models";

import { useOrganisations } from "./OrganisationsProvider";
import { useApi } from "../hooks/useApi";

interface IOrganisationContext {
  id: string;
  organisation?: FirestoreOrganisation;
  members: IUser[];
  loading: boolean;

  addUsers(users: OrganisationUserCreateRequest[]): Promise<void>;

  deleteUser(userId: string): Promise<void>;

  addFile(file: File): Promise<AccountOrganisationFileResponse | undefined>;

  triggerAcpExport(): Promise<AcpExportResponse | undefined>;

  updateUserRole(userId: string, role: UserRole): Promise<void>;
  getUsersByEmail(emails: string[]): Promise<IUser[]>;
  transferUser(
    sourceId: string,
    destinationId: string,
    userId: string,
  ): Promise<AccountUserResponse | undefined>;

  updateOrganisationSettings(
    settings: FirestoreOrganisationSettings,
  ): Promise<void>;
}

export const OrganisationContext = createContext<IOrganisationContext>(
  undefined as never,
);

interface OrganisationProviderProps {
  id: string;
}

export const OrganisationProvider = ({
  id,
  children,
}: PropsWithChildren<OrganisationProviderProps>) => {
  const { organisations, loading } = useOrganisations();

  const api = useApi();

  const organisation = useMemo(
    () => organisations.find((o) => o.id === id),
    [organisations, id],
  );

  const [members = []] = useCollectionData(
    query(
      getUsersCollection(),
      where("organisation.id", "==", id),
      where("deletedAt", "==", null),
      orderBy("email"),
    ),
  );

  const getUsersByEmail = useCallback(
    async (emails: string[]) => {
      if (!organisation) {
        return [];
      }

      // Firestore has a limit of 10 items for 'in' queries
      // Split emails into chunks of 10 and execute multiple queries if needed
      const emailChunks: string[][] = [];
      const chunkSize = 10;

      for (let i = 0; i < emails.length; i += chunkSize) {
        emailChunks.push(emails.slice(i, i + chunkSize));
      }
      const userPromises = emailChunks.map((chunk) => {
        const usersQuery = query(
          getUsersCollection(),
          where("email", "in", chunk),
        );
        return getDocs(usersQuery).then((result) =>
          result.docs.map((user) => user.data()),
        );
      });
      const userChunks = await Promise.all(userPromises);
      return userChunks.flat();
    },
    [organisation],
  );

  const transferUser = useCallback(
    async (sourceId: string, destinationId: string, userId: string) => {
      if (!organisation) {
        return;
      }

      const response = await api.account.organisations.transferUser(
        sourceId,
        destinationId,
        userId,
      );
      return response ?? undefined;
    },
    [organisation, api],
  );

  const addFile = useCallback(
    async (file: File) => {
      if (!organisation) {
        return;
      }
      const response = await api.account.organisations.addFile(
        organisation.id,
        file,
      );
      return response ?? undefined;
    },
    [organisation, api],
  );

  const triggerAcpExport = useCallback(async () => {
    if (!organisation) {
      return;
    }
    const response = await api.account.organisations.triggerAcpExport(
      organisation.id,
    );
    return response ?? undefined;
  }, [organisation, api]);

  const addUsers = useCallback(
    async (users: OrganisationUserCreateRequest[]) => {
      if (!organisation) {
        return;
      }

      await api.account.organisations.addUsers(organisation.id, {
        users,
      });
    },
    [organisation, api],
  );

  const deleteUser = useCallback(
    async (userId: string) => {
      if (!organisation) {
        return;
      }

      await api.account.organisations.deleteUser(organisation.id, userId);
    },
    [organisation, api],
  );

  const updateUserRole = useCallback(
    async (userId: string, role: UserRole) => {
      if (!organisation) {
        return;
      }

      await api.account.organisations.updateUserRole(
        organisation.id,
        userId,
        role,
      );
    },
    [organisation, api],
  );

  const updateOrganisationSettings = useCallback(
    (body: FirestoreOrganisationSettings) => {
      if (!organisation) {
        return Promise.resolve();
      }
      return api.account.organisations.updateOrganisationSettings(
        organisation.id,
        body,
      );
    },
    [organisation, api],
  );

  const value = useMemo(
    () => ({
      id,
      members,
      organisation,
      loading,
      addUsers,
      deleteUser,
      addFile,
      triggerAcpExport,
      updateUserRole,
      getUsersByEmail,
      transferUser,
      updateOrganisationSettings,
    }),
    [
      id,
      members,
      organisation,
      loading,
      addUsers,
      deleteUser,
      addFile,
      triggerAcpExport,
      updateUserRole,
      getUsersByEmail,
      transferUser,
      updateOrganisationSettings,
    ],
  );

  return <OrganisationContext value={value}>{children}</OrganisationContext>;
};

export const useOrganisation = () => {
  const context = use(OrganisationContext);

  if (!context) {
    throw new Error(
      "useOrganisation must be used within a OrganisationProvider",
    );
  }

  return context;
};
